import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { _postInvitation } from '@/api/entity/mutations';
import { ENTITY_QUERY_KEYS } from '@/api/entity/queries';
import { EntityRole, EntityTeamLegalRole, GetEntityTeamRes } from '@/api/entity/types';
import { ENTITY_VIEWS_QUERY_KEYS } from '@/api/entity-views/queries';
import { EMAIL_REGEX } from '@/constants/static';
import { useGlobalModal } from '@/global-hooks/use-global-modal';
import { Drawer } from '@/shared/design-system';
import { useEntitySlice } from '@/stores/selectors';

import { TeamMemberAddForm } from './team-member-add-form';
import { TeamMemberAddInvitationInfo } from './team-member-add-invitation-info';

export interface TeamMemberAddData {
  email: string;
  role: EntityRole | '';
  legalRole: EntityTeamLegalRole | '';
}

const initialState: TeamMemberAddData = { email: '', legalRole: '', role: '' };

export type TeamMemberAddProps = {
  entityTeam: GetEntityTeamRes;
  entityId?: string | null;
};

export const TeamMemberAdd = () => {
  const { open, handleClose, props } = useGlobalModal('TEAM_MEMBER_ADD_MODAL');
  const queryClient = useQueryClient();

  const [newTeamMember, setNewTeamMember] = useState(initialState);
  const [step, setStep] = useState<'form' | 'invitation'>('form');
  const [formError, setFormError] = useState('');

  const { mutate } = useMutation({ mutationFn: _postInvitation });
  const { stepForms } = useEntitySlice();
  const entityId = props?.entityId || stepForms.id;

  useEffect(() => {
    setFormError('');
  }, [newTeamMember.email]);

  const isFormValid =
    EMAIL_REGEX.test(newTeamMember.email) &&
    newTeamMember.email.length > 1 &&
    newTeamMember.role !== '' &&
    newTeamMember.legalRole !== '';

  const handleSendInvitation = () => {
    setFormError('');
    mutate(
      {
        entityId,
        email: newTeamMember.email,
        title: 'Team Member',
        role: newTeamMember.role as EntityRole,
        legalRole: newTeamMember.legalRole as EntityTeamLegalRole,
      },
      {
        onSuccess: () => {
          setStep('invitation');
          queryClient.invalidateQueries({ queryKey: [ENTITY_QUERY_KEYS.entities, entityId] });
          queryClient.invalidateQueries({
            queryKey: [ENTITY_VIEWS_QUERY_KEYS.teamAssociates, entityId],
          });
        },
        onError: (err: unknown) => {
          const errMessage = (err as { response?: { _data?: { data?: { details?: string } } } })
            ?.response?._data?.data?.details;
          if (errMessage) {
            setFormError(errMessage);
            setStep('form');
          }
        },
      },
    );
  };

  const handleCloseClick = () => {
    setNewTeamMember(initialState);
    setFormError('');
    setStep('form');
    handleClose();
  };

  const isFormStep = step === 'form';

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => !next && handleCloseClick()}
      title="Add New Team Member"
      showClose
      secondaryAction={isFormStep ? { children: 'Cancel', onClick: handleCloseClick } : undefined}
      primaryAction={
        isFormStep
          ? {
              children: 'Send Invitation',
              onClick: handleSendInvitation,
              disabled: !isFormValid,
            }
          : { children: 'Ok', onClick: handleCloseClick }
      }
    >
      {isFormStep ? (
        <TeamMemberAddForm data={newTeamMember} setData={setNewTeamMember} resError={formError} />
      ) : (
        <TeamMemberAddInvitationInfo email={newTeamMember.email} />
      )}
    </Drawer>
  );
};
